"use client";

import { c as _c } from "react/compiler-runtime";
import { ESRLoadingComponent, ESRProvider, Experience, type ExperienceConfiguration } from "@ninetailed/experience.js-react";
import type { ComponentType } from "react";
import type { VariantData } from "@/lib/ninetailed/mapEntryExperiences";
import { LivePersonalizedEntry, type PersonalizedViewProps } from "./LivePersonalizedEntry";
import { VariantViewedTracker } from "./VariantViewedTracker";
interface Props<TFields> {
  baseline: VariantData<TFields>;
  experiences: ExperienceConfiguration<VariantData<TFields>>[];
  exposures: Record<string, number>;
  view: ComponentType<PersonalizedViewProps<TFields>>;
}

// Explicit generics: with no passthroughProps, the SDK's default makes every
// prop a passthrough, which would drop `fields` from the baseline. Pinning an
// empty passthrough type keeps `fields` on the baseline and the variants.
//
// `exposures` is the server-resolved Record<experienceId, variantIndex> for
// every arm (baseline `0` and variants `N`). `ESRProvider` only overrides first
// paint for actual variant selections, so we filter to `index > 0` for it; the
// full map (including baseline) drives exposure tracking.
export function PersonalizedEntry(t0) {
  const $ = _c(17);
  const {
    baseline,
    experiences,
    exposures,
    view: View
  } = t0;
  let t1;
  if ($[0] !== exposures) {
    t1 = Object.fromEntries(Object.entries(exposures).filter(_temp));
    $[0] = exposures;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  const variantsMap = t1;
  let t2;
  if ($[2] !== View) {
    t2 = t3 => {
      const {
        id,
        fields
      } = t3;
      return <LivePersonalizedEntry variant={{
        id,
        fields
      }} view={View} />;
    };
    $[2] = View;
    $[3] = t2;
  } else {
    t2 = $[3];
  }
  let t3;
  if ($[4] !== baseline.fields || $[5] !== baseline.id || $[6] !== experiences || $[7] !== t2) {
    t3 = <Experience id={baseline.id} fields={baseline.fields} experiences={experiences} loadingComponent={ESRLoadingComponent} component={t2} />;
    $[4] = baseline.fields;
    $[5] = baseline.id;
    $[6] = experiences;
    $[7] = t2;
    $[8] = t3;
  } else {
    t3 = $[8];
  }
  let t4;
  if ($[9] !== baseline || $[10] !== experiences || $[11] !== exposures || $[12] !== t3) {
    t4 = <VariantViewedTracker experiences={experiences} exposures={exposures} baseline={baseline}>{t3}</VariantViewedTracker>;
    $[9] = baseline;
    $[10] = experiences;
    $[11] = exposures;
    $[12] = t3;
    $[13] = t4;
  } else {
    t4 = $[13];
  }
  let t5;
  if ($[14] !== t4 || $[15] !== variantsMap) {
    t5 = <ESRProvider experienceVariantsMap={variantsMap}>{t4}</ESRProvider>;
    $[14] = t4;
    $[15] = variantsMap;
    $[16] = t5;
  } else {
    t5 = $[16];
  }
  return t5;
}
function _temp(t0) {
  const [, index] = t0;
  return index > 0;
}